<template>
    <div class="feedback-inbox">
        <div class="d-flex flex-wrap align-items-center mb-3">
            <b-form-select
                v-model="statusFilter"
                :options="statusFilterOptions"
                style="max-width: 180px"
                class="mr-2"
                @change="fetchFeedback"
            />
            <span class="mr-auto">
                <span class="text-muted">총 {{ feedback.length }}건 / 선택</span>
                <span :class="selectionAtLimit ? 'text-warning font-weight-bold' : 'text-muted'">
                    {{ selectedIds.length }}/{{ MAX_SELECT }}건
                </span>
                <span v-if="selectionAtLimit" class="text-warning small ml-1">
                    (상한 도달 — 더 고르려면 먼저 반영하거나 선택을 해제하세요)
                </span>
            </span>

            <!-- ⛔ 반영 대상(판례/템플릿) 선택을 여기 두지 않는다 — 이 화면을 쓰는 사람은
                 ESG 평가자이고, 어디에 반영할지는 AI 가 정한다. -->
            <b-button
                variant="primary"
                class="mr-2"
                :disabled="!selectedIds.length"
                @click="openPreview"
            >
                AI 판단 기준에 반영
            </b-button>
            <b-button
                variant="outline-secondary"
                class="mr-2"
                :disabled="!selectedIds.length"
                @click="dismissSelected"
            >
                기각
            </b-button>
            <b-button variant="outline-secondary" @click="openManualModal">
                수동 피드백 추가
            </b-button>
        </div>

        <!-- 이어보기 — 페이지를 이탈했다 돌아와도 진행 중이던 개선 제안을 놓치지 않는다.
             상태는 서버(proposal)가 보관하므로 proposal id 만 있으면 그대로 이어본다. -->
        <b-alert :show="!!pendingProposalId && !previewOpen" variant="info" class="py-2">
            <div class="d-flex align-items-center">
                <span class="mr-auto">
                    이 브라우저에서 진행 중인 개선 제안(#{{ pendingProposalId }})이 있습니다.
                    이어서 확인할 수 있습니다.
                </span>
                <b-button size="sm" variant="primary" class="mr-2" @click="resumePreview">
                    이어보기
                </b-button>
                <b-button size="sm" variant="outline-secondary" @click="dismissPendingProposal">
                    지우기
                </b-button>
            </div>
        </b-alert>

        <b-alert :show="truncated" variant="warning" class="py-2">
            서버가 한 상태당 {{ MAX_ROWS }}건까지만 내려줍니다. 최신 {{ MAX_ROWS }}건만 표시 중이며,
            반영하거나 기각해 처리한 만큼 이전 피드백이 다시 나타납니다.
        </b-alert>

        <!-- 한 페이지 = 한 번에 반영 가능한 최대 건수(20). -->
        <b-table
            :items="feedback"
            :fields="fields"
            :busy="loading"
            :per-page="MAX_SELECT"
            :current-page="currentPage"
            hover
            responsive
            show-empty
            empty-text="표시할 피드백이 없습니다."
        >
            <template #head(selected)>
                <b-form-checkbox
                    :checked="allPageChecked"
                    :disabled="!pageSelectableIds.length"
                    @change="toggleAll"
                />
            </template>

            <template #cell(selected)="row">
                <b-form-checkbox
                    :checked="selectedIds.includes(row.item.id)"
                    :disabled="isCheckDisabled(row.item)"
                    @change="toggleOne(row.item.id)"
                />
            </template>

            <!-- 판정 흐름 — AI 가 뭐라 했고 평가자가 어떻게 했는지. -->
            <template #cell(judgement)="row">
                <div v-if="row.item.ai_status" class="small">
                    <div class="text-muted">AI {{ judgementLabel(row.item.ai_status) }}</div>
                    <div v-if="row.item.evaluator_status" class="font-weight-bold text-danger">
                        → {{ judgementLabel(row.item.evaluator_status) }}
                    </div>
                    <div v-else class="text-muted">→ 변경 없음</div>
                </div>
                <span v-else class="small text-muted">-</span>
            </template>

            <template #cell(source_type)="row">
                <b-badge :variant="sourceVariant(row.item.source_type)">
                    {{ sourceLabel(row.item.source_type) }}
                </b-badge>
            </template>

            <template #cell(sub_code)="row">
                {{ row.item.sub_code || '-' }}
            </template>

            <template #cell(scope)="row">
                <div class="small text-muted">
                    <div>캠페인 {{ row.item.campaign_id || '-' }}</div>
                    <div>기업 {{ row.item.company_id || '-' }}</div>
                </div>
            </template>

            <template #cell(content)="row">
                <div class="feedback-content">{{ row.item.content }}</div>
            </template>

            <!-- 반영 상태 — 서버 파생 feedback.status 를 세분화한 진행 배지 + 초안별 보조문구.
                 ⛔ 첫 초안 저장 후에도 피드백이 사라지지 않고 여기 부분진행으로 남는다. -->
            <template #cell(status)="row">
                <b-badge :variant="progressVariant(row.item)">
                    {{ progressLabel(row.item) }}
                </b-badge>
                <div
                    v-if="row.item.proposal && row.item.proposal.targets.length"
                    class="small text-muted mt-1"
                >
                    <div>
                        진행 {{ terminalCount(row.item.proposal) }}/{{ row.item.proposal.targets.length }}
                    </div>
                    <div v-for="t in row.item.proposal.targets" :key="t.id">
                        {{ previewTargetLabel(t.asset_type) }}: {{ targetStatusLabel(t.status) }}
                    </div>
                </div>
                <div v-else-if="appliedList(row.item).length" class="small text-muted mt-1">
                    <span v-for="(a, i) in appliedList(row.item)" :key="i" class="mr-2">
                        {{ previewTargetLabel(a.asset_type) }} v{{ a.version }}
                    </span>
                </div>
                <b-button
                    v-if="hasOpenProposal(row.item)"
                    size="sm"
                    variant="link"
                    class="p-0 mt-1"
                    @click="continueProposal(row.item)"
                >
                    개선안 계속 보기
                </b-button>
            </template>

            <template #cell(created_at)="row">
                {{ formatDateTime(row.item.created_at) }}
            </template>
        </b-table>

        <div v-if="feedback.length > MAX_SELECT" class="d-flex align-items-center">
            <b-pagination
                v-model="currentPage"
                :total-rows="feedback.length"
                :per-page="MAX_SELECT"
                size="sm"
                class="mb-0"
            />
            <span class="ml-3 text-muted small">
                한 페이지 {{ MAX_SELECT }}건. 선택은 페이지를 넘겨도 유지되며,
                한 번에 {{ MAX_SELECT }}건까지 반영할 수 있습니다.
            </span>
        </div>

        <!-- 개선 제안 상세 — 신규(feedbackIds) / 이어보기(proposalId) / 계속(initialProposal). -->
        <ApplyPreviewModal
            v-if="previewOpen"
            :feedback-ids="selectedIds"
            :feedback-items="selectedItems"
            :initial-proposal="modalProposal"
            :proposal-id="modalProposalId"
            @proposal-created="onProposalCreated"
            @proposal-completed="onProposalCompleted"
            @close="onModalClose"
        />

        <!-- 수동 피드백 추가 -->
        <b-modal
            id="manual-feedback-modal"
            title="수동 피드백 추가"
            ok-title="등록"
            cancel-title="취소"
            @ok="createManualFeedback"
        >
            <b-form-group label="피드백 내용" label-for="manual-feedback-content">
                <b-form-textarea
                    id="manual-feedback-content"
                    v-model="manualForm.content"
                    rows="5"
                    placeholder="예: 인증서 만료일이 지난 첨부는 보완 필요로 판정해야 한다."
                />
            </b-form-group>
            <b-row>
                <b-col cols="4">
                    <b-form-group label="문항 코드" label-for="manual-feedback-sub">
                        <b-form-input id="manual-feedback-sub" v-model="manualForm.sub_code" placeholder="선택" />
                    </b-form-group>
                </b-col>
                <b-col cols="4">
                    <b-form-group label="캠페인 ID" label-for="manual-feedback-campaign">
                        <b-form-input
                            id="manual-feedback-campaign"
                            v-model="manualForm.campaign_id"
                            type="number"
                            placeholder="선택"
                        />
                    </b-form-group>
                </b-col>
                <b-col cols="4">
                    <b-form-group label="기업 ID" label-for="manual-feedback-company">
                        <b-form-input
                            id="manual-feedback-company"
                            v-model="manualForm.company_id"
                            type="number"
                            placeholder="선택"
                        />
                    </b-form-group>
                </b-col>
            </b-row>
        </b-modal>

    </div>
</template>

<script>
import { getErrorMessage } from '@core/utils/api/errorMessage';
import ApplyPreviewModal from './ApplyPreviewModal.vue';
import {
    ACCEPTED_TARGET_TERMINALS,
    SOURCE_TYPE_VARIANTS,
    feedbackProgressLabel,
    feedbackProgressVariant,
    formatDateTime,
    judgementLabel,
    previewTargetLabel,
    sourceTypeLabel,
    targetStatusLabel,
} from './prompt-labels';
import {clearActiveProposal, loadActiveProposal} from './preview-job-store';

// 백엔드 list_feedback 이 한 번에 내려주는 상한 (status 필터 적용 후 id desc 500건).
const MAX_ROWS = 500;

// 한 번에 반영할 수 있는 피드백 상한.
// ⛔ 백엔드 _MAX_PREVIEW_FEEDBACKS 와 같은 값이어야 한다.
const MAX_SELECT = 20;

// "작업함"이 모으는 서버 파생 원시 상태(아직 처리 중인 것들).
// 백엔드가 status 필터를 적용한 뒤 500건을 자르므로, status=ALL 로 받아 로컬 필터링하면
// 최근 APPLIED/DISMISSED 가 500건을 채웠을 때 오래된 미처리분을 놓친다 — 상태별로 각각 조회한다.
const WORKING_STATUSES = ['PENDING', 'IN_PROGRESS', 'READY'];

export default {
    name: 'FeedbackInboxTab',
    components: {ApplyPreviewModal},
    data() {
        return {
            MAX_ROWS,
            MAX_SELECT,
            feedback: [],
            loading: false,
            // 조회가 서버 500건 상한에 걸려 잘렸는지 — 경고 배너 표시용.
            truncated: false,
            statusFilter: 'OPEN',
            currentPage: 1,
            selectedIds: [],
            previewOpen: false,
            // 모달 진입 파라미터 — 신규는 둘 다 null(selectedIds 사용), 이어보기/계속은 하나 지정.
            modalProposal: null,
            modalProposalId: null,
            // 이 브라우저에 남아 있는 진행 중 개선 제안 id — 이어보기 배너용.
            pendingProposalId: null,
            manualForm: {content: '', sub_code: '', campaign_id: '', company_id: ''},
            statusFilterOptions: [
                // ⛔ 영문 ENUM 을 라벨에 노출하지 않는다.
                {value: 'OPEN', text: '작업함'},
                {value: 'PENDING', text: '반영 대기'},
                {value: 'APPLIED', text: '반영됨'},
                {value: 'DISMISSED', text: '기각'},
                {value: 'ALL', text: '전체'},
            ],
            fields: [
                {key: 'selected', label: '', thStyle: {width: '40px'}},
                {key: 'source_type', label: '출처', thStyle: {width: '110px'}},
                {key: 'sub_code', label: '문항', thStyle: {width: '100px'}},
                {key: 'judgement', label: '판정', thStyle: {width: '130px'}},
                {key: 'scope', label: '대상', thStyle: {width: '100px'}},
                {key: 'content', label: '내용'},
                {key: 'status', label: '반영', thStyle: {width: '160px'}},
                {key: 'created_at', label: '등록', thStyle: {width: '130px'}},
            ],
        };
    },
    computed: {
        // 현재 페이지에 보이는 행 — 선택은 이 범위에만 작용한다.
        pageRows() {
            const start = (this.currentPage - 1) * MAX_SELECT;
            return this.feedback.slice(start, start + MAX_SELECT);
        },
        pageSelectableIds() {
            return this.pageRows.filter(this.isSelectable).map((f) => f.id);
        },
        allPageChecked() {
            return this.pageSelectableIds.length > 0
                && this.pageSelectableIds.every((id) => this.selectedIds.includes(id));
        },
        selectedItems() {
            return this.feedback.filter((f) => this.selectedIds.includes(f.id));
        },
        selectionAtLimit() {
            return this.selectedIds.length >= MAX_SELECT;
        },
    },
    created() {
        this.fetchFeedback();
        // 진행 중이던 개선 제안이 이 브라우저에 남아 있는지 확인한다.
        this.pendingProposalId = loadActiveProposal();
    },
    methods: {
        formatDateTime,
        judgementLabel,
        previewTargetLabel,
        targetStatusLabel,
        sourceLabel: sourceTypeLabel,
        progressLabel: feedbackProgressLabel,
        progressVariant: feedbackProgressVariant,
        async fetchFeedback() {
            this.loading = true;
            try {
                if (this.statusFilter === 'OPEN') {
                    // 작업함: 진행 중 3상태를 각각 조회해 id 로 병합(상태별 500 상한 안에서 안전).
                    const lists = await Promise.all(
                        WORKING_STATUSES.map((s) => this.fetchByStatus(s)));
                    this.truncated = lists.some((l) => l.length >= MAX_ROWS);
                    const byId = new Map();
                    lists.flat().forEach((f) => byId.set(f.id, f));
                    this.feedback = [...byId.values()].sort((a, b) => b.id - a.id);
                } else {
                    const rows = await this.fetchByStatus(this.statusFilter);
                    this.truncated = rows.length >= MAX_ROWS;
                    this.feedback = rows;
                }
                this.currentPage = 1;
                // 목록이 바뀌면 사라진 행의 선택은 버린다.
                const ids = new Set(this.feedback.map((f) => f.id));
                this.selectedIds = this.selectedIds.filter((id) => ids.has(id));
            } catch (e) {
                this.$bvToast.toast(getErrorMessage(e, '피드백 목록을 불러오지 못했습니다.'), {
                    title: '오류', variant: 'danger',
                });
            } finally {
                this.loading = false;
            }
        },
        async fetchByStatus(status) {
            const {data} = await this.$axios.get('/supplement-prompt/feedback', {
                params: {status},
            });
            return (data && data.feedback) || [];
        },
        // 열린 개선 제안에 든 피드백은 서버가 IN_PROGRESS/READY 로 내려주지만,
        // 경쟁 상태 방어로 open proposal 검사도 함께 건다.
        hasOpenProposal(item) {
            return !!(item.proposal && item.proposal.status === 'OPEN');
        },
        isSelectable(item) {
            return item.status === 'PENDING' && !this.hasOpenProposal(item);
        },
        isCheckDisabled(item) {
            if (!this.isSelectable(item)) return true;
            if (this.selectedIds.includes(item.id)) return false;  // 해제는 항상 가능
            return this.selectedIds.length >= MAX_SELECT;
        },
        terminalCount(proposal) {
            return (proposal.targets || []).filter(
                (t) => ACCEPTED_TARGET_TERMINALS.includes(t.status)).length;
        },
        toggleOne(id) {
            const idx = this.selectedIds.indexOf(id);
            if (idx >= 0) {
                this.selectedIds.splice(idx, 1);
                return;
            }
            if (this.selectedIds.length >= MAX_SELECT) {
                this.$bvToast.toast(
                    `한 번에 ${MAX_SELECT}건까지 반영할 수 있습니다. 나눠서 진행해 주세요.`,
                    {title: '선택 상한', variant: 'warning'});
                return;
            }
            this.selectedIds.push(id);
        },
        toggleAll() {
            if (this.allPageChecked || this.selectedIds.length) {
                this.selectedIds = [];
                return;
            }
            const room = MAX_SELECT - this.selectedIds.length;
            const picked = this.pageSelectableIds.slice(0, room);
            this.selectedIds = [...this.selectedIds, ...picked];
            if (picked.length < this.pageSelectableIds.length) {
                this.$bvToast.toast(
                    `한 번에 ${MAX_SELECT}건까지 반영할 수 있어 ${picked.length}건만 추가했습니다.`,
                    {title: '선택 상한', variant: 'info'});
            }
        },
        // 신규 반영 — 선택한 피드백으로 새 개선 제안을 만든다.
        openPreview() {
            if (!this.selectedIds.length) return;
            this.modalProposal = null;
            this.modalProposalId = null;
            this.previewOpen = true;
        },
        // 특정 피드백 행의 진행 중 개선 제안을 이어서 본다.
        continueProposal(item) {
            if (!this.hasOpenProposal(item)) return;
            this.modalProposal = item.proposal;
            this.modalProposalId = null;
            this.previewOpen = true;
        },
        // localStorage 이어보기 — proposal id 로 재수화한다.
        resumePreview() {
            if (!this.pendingProposalId) return;
            this.modalProposal = null;
            this.modalProposalId = this.pendingProposalId;
            this.previewOpen = true;
        },
        // 반영된 자산 표시 — 새 applied_assets(다중) 우선, 없으면 레거시 단일 폴백.
        appliedList(item) {
            if (Array.isArray(item.applied_assets) && item.applied_assets.length) {
                return item.applied_assets;
            }
            if (item.applied_asset_type) {
                return [{asset_type: item.applied_asset_type, version: item.applied_version}];
            }
            return [];
        },
        dismissPendingProposal() {
            clearActiveProposal();
            this.pendingProposalId = null;
        },
        onProposalCreated() {
            // 새 개선 제안이 만들어지면 선택은 소진된다(그 피드백들은 이제 진행 중).
            this.selectedIds = [];
            this.pendingProposalId = loadActiveProposal();
        },
        onProposalCompleted() {
            this.pendingProposalId = loadActiveProposal();
        },
        onModalClose() {
            this.previewOpen = false;
            this.modalProposal = null;
            this.modalProposalId = null;
            // 모달이 store 를 갱신했을 수 있으니 배너 상태를 다시 읽고 목록을 새로고침한다.
            this.pendingProposalId = loadActiveProposal();
            this.fetchFeedback();
        },
        async dismissSelected() {
            const ids = [...this.selectedIds];
            const ok = await this.$bvModal.msgBoxConfirm(
                `선택한 ${ids.length}건을 기각 처리합니다. 인박스 대기 목록에서 제외됩니다.`,
                {title: '기각 확인', okTitle: '기각', cancelTitle: '취소'},
            );
            if (!ok) return;

            let failed = 0;
            for (const id of ids) {
                try {
                    // 순차 처리 — 부분 실패해도 성공분은 그대로 반영된다.
                    // eslint-disable-next-line no-await-in-loop
                    await this.$axios.patch(`/supplement-prompt/feedback/${id}`, {status: 'DISMISSED'});
                } catch (e) {
                    failed += 1;
                }
            }
            if (failed) {
                this.$bvToast.toast(`${ids.length}건 중 ${failed}건 기각에 실패했습니다.`, {
                    title: '일부 실패', variant: 'warning',
                });
            } else {
                this.$bvToast.toast(`${ids.length}건을 기각했습니다.`, {title: '완료', variant: 'success'});
            }
            this.selectedIds = [];
            await this.fetchFeedback();
        },
        openManualModal() {
            this.manualForm = {content: '', sub_code: '', campaign_id: '', company_id: ''};
            this.$bvModal.show('manual-feedback-modal');
        },
        async createManualFeedback(evt) {
            const content = (this.manualForm.content || '').trim();
            if (!content) {
                evt.preventDefault();
                this.$bvToast.toast('피드백 내용을 입력해 주세요.', {title: '입력 필요', variant: 'warning'});
                return;
            }
            const toInt = (v) => (v === '' || v === null ? null : Number(v));
            try {
                await this.$axios.post('/supplement-prompt/feedback', {
                    content,
                    sub_code: this.manualForm.sub_code || null,
                    campaign_id: toInt(this.manualForm.campaign_id),
                    company_id: toInt(this.manualForm.company_id),
                });
                this.$bvToast.toast('피드백을 등록했습니다.', {title: '완료', variant: 'success'});
                await this.fetchFeedback();
            } catch (e) {
                this.$bvToast.toast(getErrorMessage(e, '피드백 등록에 실패했습니다.'), {
                    title: '오류', variant: 'danger',
                });
            }
        },
        sourceVariant(type) {
            return SOURCE_TYPE_VARIANTS[type] || 'secondary';
        },
    },
};
</script>

<style scoped>
/* 발송 문구 수정 피드백은 "[변경 전]/[변경 후]" 두 문단이라 줄바꿈을 살리고,
   지나치게 긴 원문은 셀 안에서만 스크롤시켜 표 높이가 무너지지 않게 한다. */
.feedback-content {
    white-space: pre-wrap;
    word-break: break-word;
    max-height: 180px;
    overflow-y: auto;
    font-size: 0.9rem;
}
</style>
